import { CollapsibleFilters } from '../../components/common/CollapsibleFilters';
import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  FormControl,
  FormLabel,
  Input,
  Option,
  Select,
  Stack,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  Typography,
} from "@mui/joy";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { TaskList } from "../../components/tasks/TaskList";
import { TaskCreateModal } from "../../components/tasks/TaskCreateModal";
import { TaskDetailModal } from "../../components/tasks/TaskDetailModal";
import { TaskTemplates } from "../../components/tasks/TaskTemplates";
import { TaskBulkActions } from "../../components/tasks/TaskBulkActions";
import {
  buildTaskListParams,
  useTasksInfiniteQuery,
  useTaskStatisticsQuery,
  type TaskQueryOptions,
} from "../../hooks/useTasks";
import { useDebounce } from "../../hooks/useDebounce";
import { useAuth } from "../../contexts/AuthContext";
import { useOrganizationUsers } from "../../hooks/useOrganizationUsers";
import { apiClient } from "../../lib/api";
import {
  getOrganizationTimezone,
  parseFromDatetimeLocalInput,
} from "../../lib/timezoneUtils";
import type { Task } from "../../types/tasks";

function GroupTasks({
  options,
  onOpen,
}: {
  options: TaskQueryOptions;
  onOpen: (task: Task) => void;
}) {
  const query = useTasksInfiniteQuery(options);
  return (
    <Stack spacing={1}>
      {query.isError ? (
        <Alert color="danger">
          Unable to load tasks.{" "}
          <Button onClick={() => query.refetch()}>Retry</Button>
        </Alert>
      ) : (
        <TaskList
          tasks={query.tasks}
          isLoading={query.isLoading}
          onTaskUpdate={() => query.refetch()}
          onOpenTask={onOpen}
        />
      )}
      <Typography level="body-xs">
        {query.tasks.length} of {query.totalCount} tasks
      </Typography>
      {query.hasNextPage && (
        <Button
          loading={query.isFetchingNextPage}
          onClick={() => query.fetchNextPage()}
        >
          Load more tasks
        </Button>
      )}
      {query.reachedLoadLimit && (
        <Alert>Narrow the filters to see more matching tasks.</Alert>
      )}
    </Stack>
  );
}
function ByJob({
  options,
  onCreate,
  onOpen,
}: {
  options: TaskQueryOptions;
  onCreate: (job?: number) => void;
  onOpen: (task: Task) => void;
}) {
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState<number | null | undefined>();
  const query = useQuery({
    queryKey: ["tasks", "job-groups", options, page],
    queryFn: async () =>
      (
        await apiClient.get<any>(
          `/api/tasks/job-groups/?${buildTaskListParams(options)}&page=${page}`,
        )
      ).data,
  });
  useEffect(() => {
    setPage(1);
    setExpanded(undefined);
  }, [JSON.stringify(options)]);
  return (
    <Stack spacing={2}>
      {query.isError && (
        <Alert color="danger">
          Unable to load job groups.{" "}
          <Button onClick={() => query.refetch()}>Retry</Button>
        </Alert>
      )}
      {query.isLoading && <Typography>Loading job groups…</Typography>}
      {query.data?.results.length === 0 && (
        <Typography>No jobs match these task filters.</Typography>
      )}
      {query.data?.results.map((group: any) => (
        <Card key={group.related_job ?? "unlinked"}>
          <Box
            sx={{
              display: "flex",
              gap: 1,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <Button
              variant="plain"
              onClick={() =>
                setExpanded(
                  expanded === group.related_job
                    ? undefined
                    : group.related_job,
                )
              }
            >
              {expanded === group.related_job ? "Hide" : "Show"}{" "}
              {group.related_job
                ? `${group.related_job__job_number} · ${group.related_job__address_1}`
                : "Unlinked tasks"}
            </Button>
            {group.related_job && (
              <Button
                component="a"
                href={`/operations/jobs/${group.related_job}?tab=tasks`}
                variant="outlined"
                size="sm"
              >
                Open job
              </Button>
            )}
            <Button
              size="sm"
              onClick={() => onCreate(group.related_job ?? undefined)}
            >
              Add task
            </Button>
          </Box>
          <Typography level="body-sm">
            {group.open} open · {group.overdue} overdue · Next due:{" "}
            {group.next_due
              ? new Date(group.next_due).toLocaleDateString()
              : "Not set"}
          </Typography>
          {expanded === group.related_job && (
            <GroupTasks
              options={{
                ...options,
                jobId: group.related_job ?? undefined,
                unlinked: group.related_job === null,
              }}
              onOpen={onOpen}
            />
          )}
        </Card>
      ))}
      <Box sx={{ display: "flex", gap: 1 }}>
        <Button
          disabled={page === 1 || query.isLoading}
          onClick={() => setPage(page - 1)}
        >
          Previous
        </Button>
        <Typography>
          Page {page} · {query.data?.count ?? "—"} groups
        </Typography>
        <Button
          disabled={!query.data?.next || query.isLoading}
          onClick={() => setPage(page + 1)}
        >
          Next
        </Button>
      </Box>
    </Stack>
  );
}

export function TasksPage() {
  const { user } = useAuth();
  const { users } = useOrganizationUsers();
  const [params, setParams] = useSearchParams();
  const [creating, setCreating] = useState(false);
  const [jobId, setJobId] = useState<number>();
  const [selected, setSelected] = useState<number[]>([]);
  const storageKey = `tasks-views:${user?.organization}:${user?.id}`;
  const [saved, setSaved] = useState<Record<string, string>>({});
  useEffect(() => {
    try {
      setSaved(JSON.parse(localStorage.getItem(storageKey) || "{}"));
    } catch {
      setSaved({});
    }
  }, [storageKey]);
  const change = (key: string, value: string) => {
    setParams((previous) => {
      const next = new URLSearchParams(previous);
      if (value) next.set(key, value);
      else next.delete(key);
      return next;
    });
  };
  const tab = ["all", "mine", "jobs", "templates"].indexOf(
    params.get("view") || "all",
  );
  const activeTab = tab < 0 ? 0 : tab;
  const search = useDebounce(params.get("search") || "", 300);
  const zone = getOrganizationTimezone();
  const toUTC = (date: string | null, end = false) => {
    if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return undefined;
    const next = new Date(`${date}T12:00:00Z`);
    if (end) next.setUTCDate(next.getUTCDate() + 1);
    return parseFromDatetimeLocalInput(
      `${next.toISOString().slice(0, 10)}T00:00`,
      zone,
    ).toISOString();
  };
  const options: TaskQueryOptions = {
    archived: params.get("archived") === "true",
    status: params.get("status") || (params.get("archived") === "true" ? "all" : "open"),
    priority: params.get("priority") || "all",
    search,
    assignedTo:
      activeTab === 1 ? user?.id : Number(params.get("assignee")) || undefined,
    queue: params.get("queue") || "",
    ordering: params.get("sort") || "due_date,id",
    dueFrom: toUTC(params.get("from")),
    dueTo: toUTC(params.get("to"), true),
  };
  const list = useTasksInfiniteQuery(options);
  const stats = useTaskStatisticsQuery(options);
  useEffect(() => setSelected([]), [JSON.stringify(options), activeTab]);
  const taskId = Number(params.get("task")) || 0;
  const detail = useQuery({
    queryKey: ["task", taskId],
    enabled: taskId > 0,
    queryFn: async () =>
      (await apiClient.get<Task>(`/api/tasks/${taskId}/`)).data,
  });
  const open = (task: Task) => change("task", String(task.id));
  const close = () => change("task", "");
  const create = (job?: number) => {
    setJobId(job);
    setCreating(true);
  };
  const listPanel = (
    <Stack spacing={2}>
      {list.isError ? (
        <Alert color="danger">
          Tasks could not be loaded.{" "}
          <Button onClick={() => list.refetch()}>Retry</Button>
        </Alert>
      ) : (
        <>
          <Checkbox
            label="Select loaded tasks (up to 100)"
            checked={
              list.tasks.length > 0 &&
              selected.length === Math.min(100, list.tasks.length)
            }
            onChange={(e) =>
              setSelected(
                e.target.checked
                  ? list.tasks.slice(0, 100).map((task) => task.id)
                  : [],
              )
            }
          />
          <TaskBulkActions
            tasks={list.tasks}
            selected={selected}
            setSelected={setSelected}
          />
          <TaskList
            tasks={list.tasks}
            isLoading={list.isLoading}
            onTaskUpdate={() => list.refetch()}
            onOpenTask={open}
            selectedIds={selected}
            onSelectTask={(id) =>
              setSelected((current) =>
                current.includes(id)
                  ? current.filter((value) => value !== id)
                  : current.length < 100
                    ? [...current, id]
                    : current,
              )
            }
          />
        </>
      )}
      <Typography level="body-sm">
        Showing {list.tasks.length} of {list.totalCount} matching tasks
      </Typography>
      {list.hasNextPage && (
        <Button
          loading={list.isFetchingNextPage}
          onClick={() => list.fetchNextPage()}
        >
          Load more tasks
        </Button>
      )}
      {list.reachedLoadLimit && (
        <Alert>Narrow your filters to reach additional matching tasks.</Alert>
      )}
    </Stack>
  );
  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1500, mx: "auto" }}>
      <Stack spacing={2}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Typography level="h2">Tasks</Typography>
            <Typography>
              Plan the work, clear blockers, and keep every job moving.
            </Typography>
          </Box>
          <Button onClick={() => create()}>Create task</Button>
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4,1fr)" },
            gap: 1,
          }}
        >
          {[
            ["Matching tasks", stats.data?.total],
            ["In progress", stats.data?.by_status.in_progress],
            ["Completed", stats.data?.by_status.completed],
            ["Overdue", stats.data?.overdue],
          ].map(([label, value]) => (
            <Card key={String(label)}>
              <Typography level="body-sm">{label}</Typography>
              <Typography level="h3">{value ?? "—"}</Typography>
            </Card>
          ))}
        </Box>
        <CollapsibleFilters><Card>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2,1fr)",
                lg: "repeat(4,1fr)",
              },
              gap: 1.5,
            }}
          >
            <FormControl>
              <FormLabel>Search</FormLabel>
              <Input
                placeholder="Search tasks…"
                value={params.get("search") || ""}
                onChange={(e) => change("search", e.target.value)}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Work queue</FormLabel>
              <Select
                value={params.get("queue") || ""}
                onChange={(_, value) => change("queue", value || "")}
              >
                {[
                  ["", "All dates"],
                  ["today", "Today"],
                  ["overdue", "Overdue"],
                  ["upcoming", "Next 7 days"],
                  ["unassigned", "Unassigned"],
                  ["blocked", "Blocked"],
                ].map(([value, label]) => (
                  <Option key={value} value={value}>
                    {label}
                  </Option>
                ))}
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel>Task Visibility</FormLabel>
              <Select value={options.archived ? "archived" : "active"} onChange={(_, value) => {
                setParams(previous => { const next = new URLSearchParams(previous); if (value === "archived") next.set("archived", "true"); else next.delete("archived"); next.delete("status"); next.delete("queue"); return next; });
              }}>
                <Option value="active">Active Tasks</Option>
                <Option value="archived">Archived Tasks</Option>
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel>Status</FormLabel>
              <Select
                value={options.status}
                onChange={(_, value) => change("status", value!)}
              >
                {[
                  "open",
                  "all",
                  "not_started",
                  "in_progress",
                  "on_hold",
                  "completed",
                  "cancelled",
                ].map((value) => (
                  <Option key={value} value={value}>
                    {value === "open" ? "All open" : value.replaceAll("_", " ")}
                  </Option>
                ))}
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel>Assignee</FormLabel>
              <Select
                disabled={activeTab === 1}
                value={options.assignedTo ? String(options.assignedTo) : ""}
                onChange={(_, value) => change("assignee", String(value || ""))}
              >
                <Option value="">All assignees</Option>
                {users.map((person) => (
                  <Option key={person.id} value={String(person.id)}>
                    {person.first_name} {person.last_name}
                  </Option>
                ))}
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel>Priority</FormLabel>
              <Select
                value={options.priority}
                onChange={(_, value) => change("priority", value!)}
              >
                {["all", "low", "medium", "high", "critical"].map((value) => (
                  <Option key={value} value={value}>
                    {value}
                  </Option>
                ))}
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel>Due from · {zone}</FormLabel>
              <Input
                type="date"
                value={params.get("from") || ""}
                onChange={(e) => change("from", e.target.value)}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Due through</FormLabel>
              <Input
                type="date"
                value={params.get("to") || ""}
                onChange={(e) => change("to", e.target.value)}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Sort</FormLabel>
              <Select
                value={options.ordering}
                onChange={(_, value) => change("sort", value!)}
              >
                <Option value="due_date,id">Due soonest</Option>
                <Option value="-created_at,-id">Newest first</Option>
                <Option value="name,id">Name</Option>
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              size="sm"
              variant="outlined"
              onClick={() => {
                const name = window.prompt("Name this saved view");
                if (name?.trim()) {
                  const snapshot = new URLSearchParams(params);
                  snapshot.delete("task");
                  const next = { ...saved, [name.trim()]: snapshot.toString() };
                  localStorage.setItem(storageKey, JSON.stringify(next));
                  setSaved(next);
                }
              }}
            >
              Save view
            </Button>
            <Select
              placeholder="Open saved view"
              value={null}
              onChange={(_, value) => {
                if (value) setParams(saved[String(value)]);
              }}
            >
              {Object.keys(saved).map((name) => (
                <Option key={name} value={name}>
                  {name}
                </Option>
              ))}
            </Select>
            <Button
              size="sm"
              variant="plain"
              onClick={() => setParams(taskId ? { task: String(taskId) } : {})}
            >
              Reset filters
            </Button>
          </Box>
        </Card></CollapsibleFilters>
        <Tabs
          value={activeTab}
          onChange={(_, value) =>
            change("view", ["all", "mine", "jobs", "templates"][Number(value)])
          }
        >
          <TabList sx={{ flexWrap: "wrap" }}>
            <Tab>All Tasks</Tab>
            <Tab>My Tasks</Tab>
            <Tab>By Job</Tab>
            <Tab>Templates</Tab>
          </TabList>
          <TabPanel value={0}>{listPanel}</TabPanel>
          <TabPanel value={1}>{listPanel}</TabPanel>
          <TabPanel value={2}>
            <ByJob options={options} onCreate={create} onOpen={open} />
          </TabPanel>
          <TabPanel value={3}>
            <TaskTemplates />
          </TabPanel>
        </Tabs>
        {creating && (
          <TaskCreateModal
            key={jobId ?? "unlinked"}
            open
            onClose={() => setCreating(false)}
            onSuccess={() => {
              setCreating(false);
              list.refetch();
            }}
            prefilledJobId={jobId}
          />
        )}
        {taskId > 0 && detail.isLoading && (
          <Alert>
            Loading task… <Button onClick={close}>Cancel</Button>
          </Alert>
        )}
        {taskId > 0 && detail.isError && (
          <Alert color="danger">
            This task is unavailable or could not be loaded.{" "}
            <Button onClick={() => detail.refetch()}>Retry</Button>
            <Button onClick={close}>Close</Button>
          </Alert>
        )}
        {taskId > 0 && detail.data && (
          <TaskDetailModal
            key={taskId}
            task={detail.data}
            open
            onClose={close}
            onUpdate={() => {
              list.refetch();
              detail.refetch();
            }}
          />
        )}
      </Stack>
    </Box>
  );
}
